"use client";

import {
  Code,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Link as LinkIcon,
  LinkOff,
  Redo,
  Subject,
  Undo,
} from "@mui/icons-material";
import {
  Box,
  FormHelperText,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState } from "react";

interface HtmlEditorProps {
  label: string;
  value: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | null;
  onChange: (value: string) => void;
}

const HtmlEditor = ({
  label,
  value,
  disabled = false,
  error = false,
  helperText,
  onChange,
}: HtmlEditorProps) => {
  const [viewMode, setViewMode] = useState<"text" | "html">("text");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: "https",
        },
      }),
    ],
    content: value,
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor: updatedEditor }) => {
      onChange(updatedEditor.isEmpty ? "" : updatedEditor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  const setLink = () => {
    if (!editor) {
      return;
    }

    const currentUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", currentUrl || "https://");

    if (url === null) {
      return;
    }

    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url.trim() })
      .run();
  };

  const toolbarButton = (
    title: string,
    icon: React.ReactNode,
    action: () => void,
    active = false,
    actionDisabled = false,
  ) => (
    <Tooltip title={title} key={title}>
      <span>
        <IconButton
          size="small"
          color={active ? "primary" : "default"}
          disabled={disabled || actionDisabled}
          onClick={action}
          aria-label={title}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: error ? "error.main" : "rgba(0, 0, 0, 0.23)",
          borderRadius: 2,
          overflow: "hidden",
          opacity: disabled ? 0.6 : 1,
          backgroundColor: disabled ? "action.disabledBackground" : "white",
          "&:focus-within": {
            borderColor: error ? "error.main" : "primary.main",
            borderWidth: 2,
          },
        }}
      >
        <Box
          role="toolbar"
          aria-label={`${label} formatting`}
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.25,
            p: 0.5,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "#f3f3f5",
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            disabled={disabled}
            onChange={(_, nextMode: "text" | "html" | null) => {
              if (nextMode) {
                setViewMode(nextMode);
              }
            }}
            aria-label={`${label} editor view`}
            sx={{ mr: 0.75 }}
          >
            <ToggleButton value="text" aria-label="Text view">
              <Subject fontSize="small" sx={{ mr: 0.5 }} />
              Text
            </ToggleButton>
            <ToggleButton value="html" aria-label="HTML view">
              <Code fontSize="small" sx={{ mr: 0.5 }} />
              HTML
            </ToggleButton>
          </ToggleButtonGroup>
          {viewMode === "text" && (
            <>
              {toolbarButton(
                "Bold",
                <FormatBold fontSize="small" />,
                () => editor?.chain().focus().toggleBold().run(),
                editor?.isActive("bold"),
                !editor?.can().chain().focus().toggleBold().run(),
              )}
              {toolbarButton(
                "Italic",
                <FormatItalic fontSize="small" />,
                () => editor?.chain().focus().toggleItalic().run(),
                editor?.isActive("italic"),
                !editor?.can().chain().focus().toggleItalic().run(),
              )}
              {toolbarButton(
                "Bulleted list",
                <FormatListBulleted fontSize="small" />,
                () => editor?.chain().focus().toggleBulletList().run(),
                editor?.isActive("bulletList"),
              )}
              {toolbarButton(
                "Numbered list",
                <FormatListNumbered fontSize="small" />,
                () => editor?.chain().focus().toggleOrderedList().run(),
                editor?.isActive("orderedList"),
              )}
              {toolbarButton(
                "Block quote",
                <FormatQuote fontSize="small" />,
                () => editor?.chain().focus().toggleBlockquote().run(),
                editor?.isActive("blockquote"),
              )}
              {toolbarButton(
                "Add link",
                <LinkIcon fontSize="small" />,
                setLink,
                editor?.isActive("link"),
              )}
              {toolbarButton(
                "Remove link",
                <LinkOff fontSize="small" />,
                () => editor?.chain().focus().unsetLink().run(),
                false,
                !editor?.isActive("link"),
              )}
              {toolbarButton(
                "Undo",
                <Undo fontSize="small" />,
                () => editor?.chain().focus().undo().run(),
                false,
                !editor?.can().chain().focus().undo().run(),
              )}
              {toolbarButton(
                "Redo",
                <Redo fontSize="small" />,
                () => editor?.chain().focus().redo().run(),
                false,
                !editor?.can().chain().focus().redo().run(),
              )}
            </>
          )}
        </Box>
        <Box
          aria-label={label}
          sx={{
            "& .tiptap": {
              minHeight: 150,
              p: 1.75,
              outline: "none",
              color: "black",
            },
            "& .tiptap p": { my: 0.75 },
            "& .tiptap ul, & .tiptap ol": { pl: 3, my: 1 },
            "& .tiptap blockquote": {
              borderLeft: "3px solid #bdbdbd",
              pl: 2,
              ml: 0,
            },
          }}
        >
          {viewMode === "text" ? (
            <EditorContent editor={editor} />
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={6}
              value={value}
              disabled={disabled}
              onChange={(event) => onChange(event.target.value)}
              slotProps={{
                htmlInput: {
                  "aria-label": `${label} HTML source`,
                  spellCheck: false,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  color: "black",
                },
              }}
            />
          )}
        </Box>
      </Box>
      <FormHelperText error={error} sx={{ mx: 1.75 }}>
        {error ? helperText : " "}
      </FormHelperText>
    </Box>
  );
};

export default HtmlEditor;
